import React from 'react';

import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';

import {
  Const,
  LegacyText,
  LegacyTextList,
  ArrowForwardIcon,
  ConfirmDialog,
} from '@gogame/components';
import { usePercentFormat } from '@gogame/intl-ts';

import { NODE_LEVELS } from 'scenes/MarketingPlanning/MarketingPlanningWizard/constants';

import { LabelsBox, LabelsListBox } from './components';

const getLabelsDepth = ({ name, activityTypes, nodes }, activityType) => {
  if (!activityTypes[activityType]) {
    return null;
  }

  return isEmpty(nodes)
    ? [name]
    : nodes.reduce((acc, node) => {
        const labelPaths = getLabelsDepth(node, activityType) || [];
        return [
          ...acc,
          ...labelPaths.map((item) => [
            name,
            ...(Array.isArray(item) ? item : [item]),
          ]),
        ];
      }, []);
};

const ConfirmApplyDialog = ({ payload, onSubmit, onExited, onClose, open }) => {
  const { activityType, metric, value, depth, metricTypes, levelValue } =
    payload;

  const formatPercent = usePercentFormat();

  const title = React.useMemo(() => {
    const valueFormatted = formatPercent(value);
    const metricLabel = metricTypes.find(({ id }) => id === metric)?.name;
    return `Apply ${metricLabel} of ${valueFormatted} to entities below?`;
  }, [metric, value, metricTypes]);

  const labelRenderList = React.useMemo(() => {
    const path = getLabelsDepth(levelValue, activityType);
    const list = [];

    let i = 0;
    let stopWhile = false;

    while (!stopWhile) {
      const uniqLabels = uniq(
        // eslint-disable-next-line
        path.map((item) => item[i]).filter((item) => !!item)
      );

      if (isEmpty(uniqLabels)) {
        stopWhile = true;
        break;
      }

      if (uniqLabels.length > 1) {
        // eslint-disable-next-line
        list.push(path.map((item) => Array.from(item).splice(i)));
        stopWhile = true;
        break;
      }

      list.push(uniqLabels[0]);
      i += 1;
    }

    return list;
  }, [levelValue, activityType]);

  const renderLabelsList = (labels, index) => {
    return (
      <LabelsListBox key={`labels_list_${index}`} gutter={4} align={'center'}>
        {labels
          .map((label, i) => (
            <LegacyText key={`labels_list_item_${i}`}>{label}</LegacyText>
          ))
          .reduce((result, item) => (
            <React.Fragment>
              {result}
              <ArrowForwardIcon sx={{ fontSize: 12 }} color={'action'} />
              {item}
            </React.Fragment>
          ))}
      </LabelsListBox>
    );
  };

  const renderLabels = (list) => {
    return !isEmpty(list)
      ? list
          .map((item, i) => {
            if (Array.isArray(item)) {
              return (
                <LegacyTextList
                  count={item.length}
                  unitsName={'Entities'}
                  limit={0}
                  position={'right'}
                >
                  {item.map(renderLabelsList)}
                </LegacyTextList>
              );
            }

            return <Const label={NODE_LEVELS[depth + i]}>{item}</Const>;
          })
          .reduce((result, item) => (
            <React.Fragment>
              {result}
              <ArrowForwardIcon color={'action'} />
              {item}
            </React.Fragment>
          ))
      : null;
  };

  return (
    <ConfirmDialog
      title={title}
      confirmActionLabel={'Apply'}
      onSubmit={onSubmit}
      onExited={onExited}
      onClose={onClose}
      open={open}
    >
      <LabelsBox gutter={16} align={'center'}>
        {renderLabels(labelRenderList)}
      </LabelsBox>
    </ConfirmDialog>
  );
};

export default ConfirmApplyDialog;
